import React, { useState, useCallback } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { validateCpf, sanitizeCpf } from '@tectonics/commons/cpf';
import { applyCpfMask } from '../utils/cpf-mask';
import { Wrapper, Label, Input, ErrorText } from './cpf-input.styles';

interface CpfInputProps {
  readonly value: string;
  readonly onChange: (rawDigits: string) => void;
  readonly disabled?: boolean;
}

const CPF_LENGTH = 11;

export function CpfInput({ value, onChange, disabled }: CpfInputProps): ReactNode {
  const [touched, setTouched] = useState(false);
  const digits = sanitizeCpf(value);
  const maskedValue = digits.length > 0 ? applyCpfMask(digits) : '';
  const isComplete = digits.length === CPF_LENGTH;
  const hasError = touched && isComplete && !validateCpf(digits);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const rawDigits = event.target.value.replace(/\D/g, '').substring(0, CPF_LENGTH);
    onChange(rawDigits);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setTouched(true);
  }, []);

  return (
    <Wrapper>
      <Label htmlFor="cpf-input">CPF</Label>
      <Input
        id="cpf-input"
        type="tel"
        inputMode="numeric"
        placeholder="000.000.000-00"
        value={maskedValue}
        onChange={handleChange}
        onBlur={handleBlur}
        hasError={hasError}
        disabled={disabled}
      />
      {hasError && (
        <ErrorText>CPF inválido. Tente novamente.</ErrorText>
      )}
    </Wrapper>
  );
}
